import { useNavigate, useSearchParams } from 'react-router-dom';
import { recordedSentAt } from '@/lib/sendEvidence';
import { useState, useEffect, useRef, useCallback } from 'react'; // Phase 6 Batch 5.3 + 10a.8
import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { ViewTabs, ViewTab } from '@/components/ui/view-tabs';
import { FilterBar } from '@/components/ui/filter-bar';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Trash2, Pencil, DollarSign, Zap, CalendarDays, X, Receipt, Combine } from 'lucide-react';

import { EmptyState } from '@/components/ui/empty-state';
import { PaymentModal } from '@/components/invoices/PaymentModal';
import { QboCsvExportDialog } from '@/components/invoices/QboCsvExportDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { z } from 'zod';
import { invoiceSchema } from '@/lib/schemas';
import { InvoiceStatusSelect } from '@/components/forms/InvoiceStatusSelect'; // Phase 6 Batch 5.3
import { InvoiceStatusBadge } from '@/components/invoices/InvoiceStatusBadge'; // Phase 6 Batch 5.3: Consistency fix
import type { InvoiceStatus } from '@/components/forms/InvoiceStatusSelect'; // Phase 6 Batch 5.3
import { MergeConfirmationDialog } from '@/components/invoices/MergeConfirmationDialog'; // SPEC-invoice-merge-2026-08-17 R3 (legacy; flag-off only)
import { InvoiceMergeReview } from '@/components/invoices/InvoiceMergeReview'; // SPEC-invoice-merge-history R4: server-plan review
import { InvoiceMergeFlow } from '@/components/invoices/InvoiceMergeFlow'; // SPEC-invoice-merge-history R4: choose a candidate, then review
import { useFeature } from '@/lib/features/FeatureProvider'; // reviewed_invoice_merge tenant flag
import { CancelInvoiceDialog } from '@/components/invoices/CancelInvoiceDialog'; // cancel-invoice feature
import { getDeleteBlockedReason } from '@/lib/invoice-delete'; // Christa-lens redirect: shared Delete-disable reason
import { SuperAdminDestructiveConfirmDialog } from '@/components/invoices/SuperAdminDestructiveConfirmDialog'; // super-admin destructive-op guard
import { useAuth } from '@/lib/auth/AuthProvider'; // super-admin destructive-op guard
import { isGuardedStatusDowngrade, statusLabel } from '@/lib/superAdminDestructiveGuard'; // super-admin destructive-op guard
import {
  useInvoices,
  useUpdateInvoiceInline,
  useDeleteInvoice,
  useCancelInvoice,
  useReferrerPage,
  useBatchUpdateInvoiceDate,
  useMergeInvoices,
  useInvoiceItems,
} from '@/hooks'; // Phase 6 Batch 5.3 + 9.15c + SPEC-invoice-merge-2026-08-17 R3 + cancel-invoice feature
import {
  determineMergeSurvivor,
  extractMergeRefusal,
  computeMergeResultingTotal,
  isMergeReviewRequired,
  type MergeRefusal,
} from '@/lib/invoice-merge';
import { usePaginationState } from '@/hooks/usePaginationState';
import { PaginationInfo } from '@/components/PaginationInfo';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination';
import { calculatePaginationValues } from '@/lib/pagination';
import { toast } from 'sonner';

// R7: Compact TO/CC recipient display for invoice list rows
interface InvoiceContact {
  role: 'to' | 'cc';
  contact_name: string;
  contact_email?: string | null;
}

function RecipientCell({ contacts }: { contacts: InvoiceContact[] }) {
  const toContact = contacts.find((c) => c.role === 'to');
  const ccContacts = contacts.filter((c) => c.role === 'cc');

  if (!toContact) {
    return <span className="text-muted-foreground">—</span>;
  }

  const ccOverflow = ccContacts.slice(1);

  return (
    <div className="flex items-center gap-1 text-sm flex-wrap">
      <span className="text-xs font-medium text-muted-foreground">TO</span>
      <span className="truncate max-w-[90px]">{toContact.contact_name}</span>
      {ccContacts.length > 0 && (
        <span className="hidden lg:inline-flex items-center gap-1">
          <span className="text-xs font-medium text-muted-foreground ml-1">CC</span>
          <span className="truncate max-w-[80px]">{ccContacts[0].contact_name}</span>
          {ccOverflow.length > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-muted-foreground cursor-default">+{ccOverflow.length}</span>
                </TooltipTrigger>
                <TooltipContent>
                  {ccOverflow.map((c, i) => (
                    <p key={i}>{c.contact_name}</p>
                  ))}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
      )}
    </div>
  );
}

// View tabs configuration
const invoiceViews: ViewTab[] = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'ready', label: 'Ready' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'all', label: 'All Invoices' },
];

type Invoice = z.infer<typeof invoiceSchema>;

export function InvoiceListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null); // Phase 6 Batch 5.3
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);
  const [paymentInvoice, setPaymentInvoice] = useState<Invoice | null>(null); // Phase 9.13c
  const updateInvoiceInline = useUpdateInvoiceInline(); // Phase 6 Batch 5.3

  // Batch date change state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showDateModal, setShowDateModal] = useState(false);
  const [datePickerMonth, setDatePickerMonth] = useState<number>(new Date().getMonth() + 1); // 1-12
  const [datePickerYear, setDatePickerYear] = useState<number>(new Date().getFullYear());
  const batchUpdateDate = useBatchUpdateInvoiceDate();

  // SPEC-invoice-merge-2026-08-17 R3: merge confirmation state
  const [mergeCandidates, setMergeCandidates] = useState<[Invoice, Invoice] | null>(null);
  const [mergeError, setMergeError] = useState<MergeRefusal | null>(null);
  const mergeMutation = useMergeInvoices();

  // SPEC-invoice-merge-history-2026-10-02 R4: with the tenant flag on, merging is a SERVER-PLAN review
  // (InvoiceMergeReview) in any lifecycle state; no eligibility, survivor or balance decision is made in
  // this file for that path. With the flag off the legacy confirmation below behaves exactly as before.
  const reviewedMerge = useFeature('reviewed_invoice_merge');
  const [reviewPair, setReviewPair] = useState<{ a: string; b: string; notice: string | null } | null>(null);
  const [reviewAnchor, setReviewAnchor] = useState<Invoice | null>(null);

  // Cancel-invoice feature: reached via a blocked merge's "Cancel invoice #N"
  // buttons (build item 10).
  const { user, can } = useAuth();
  // Christa-lens redirect (2026-09-15): cancelling AND deleting both need
  // can_delete_invoices -- gate both the row Delete button and the
  // merge-blocked "Cancel invoice #N" offers the same way.
  const canDeleteInvoices = can('can_delete_invoices');
  const isSuperAdmin = !!user?.is_super_admin;
  const cancelMutation = useCancelInvoice();
  const [cancelTarget, setCancelTarget] = useState<Invoice | null>(null);
  // Christa-lens redirect (2026-09-15): carries the typed reason alongside
  // the invoice -- the super-admin confirm step used to drop it, always
  // sending an empty reason regardless of what was typed in CancelInvoiceDialog.
  const [pendingCancelSuperAdmin, setPendingCancelSuperAdmin] = useState<{ invoice: Invoice; reason?: string } | null>(null);
  // Christa-lens redirect: the dialog's inline error slot existed but was
  // never wired up on this page either.
  const [cancelError, setCancelError] = useState<{ message: string } | null>(null);

  // Read filters from URL params
  const currentView = searchParams.get('view') || 'all';
  const search = searchParams.get('search') || '';
  // Phase 9.13b: Sort state from URL
  // QW-4: Default sort by invoice_number (Christa feedback 2024-12-22)
  const sortBy = searchParams.get('sort') || 'invoice_number';
  const sortOrder = searchParams.get('order') || 'desc';

  // Phase 6: Pagination state management in URL
  const { page, limit, offset, setPage } = usePaginationState(50);
  const { storeCurrentPage } = useReferrerPage('invoices'); // 9.15c: Store page for back nav

  // 10a.8: Scroll container ref for scroll-to-top on page change.
  // Sticky-scrollbar machinery now lives inside the shared DataTable.
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  // 10a.8: Scroll-to-top on page change
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setTimeout(() => {
      tableContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, [setPage]);

  // Determine status filter based on current view
  let statusFilter: string | undefined;
  switch (currentView) {
    case 'unpaid':
      // V168: Unpaid = sent OR partially_paid (both have outstanding money owed).
      // Backend get_invoices supports comma-separated multi-status filtering.
      statusFilter = 'sent,partially_paid';
      break;
    case 'ready':
      statusFilter = 'ready';
      break;
    case 'paid':
      statusFilter = 'paid';
      break;
    case 'overdue':
      statusFilter = 'overdue';  // Backend handles overdue logic
      break;
    // 'all': no status filter
  }

  // Fetch invoices with backend filtering and pagination (Phase 6)
  // Phase 9.13b: Added sort params
  const { data, isLoading, error } = useInvoices({
    status: statusFilter,
    search: search || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
    limit,
    offset,
  }, {
    keepPreviousData: true, // Smooth page transitions
  });

  const invoices = data?.invoices || [];
  const total = data?.total || 0;

  // SPEC-invoice-merge-2026-08-17 (2026-09-04, H1): drop the selection whenever
  // the rendered row set changes.
  //
  // `selectedIds` is component state but `invoices` is only the CURRENT page
  // (50 rows, default sort invoice_number desc). Every consumer of the
  // selection -- the "N invoices selected" bar, the merge button, and
  // handleOpenMergeDialog's `invoices.filter(...)` -- resolves ids against that
  // page alone. Without this, selecting one invoice, then paging or searching
  // to find the second, left the bar and the button affirming a two-invoice
  // selection while the handler could resolve only one row and returned
  // silently. That is the most natural route to this feature ("I think I saw
  // merging as a new option but can't find in the portal", 2026-08-27; search
  // is how she locates a specific invoice) and on it the button did nothing at
  // all -- with no message. An interface that fails to act without saying so is
  // the single thing this operator has been trained by this product to distrust.
  //
  // Clearing is the honest fix: the selection never outlives the rows it was
  // made against, so what the UI affirms is always what the handler can act on.
  // It also corrects the same latent bug in the batch-date selection, which
  // predates the merge feature.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, currentView, search, sortBy, sortOrder]);

  // Calculate pagination values
  const paginationValues = calculatePaginationValues(total, offset, limit);

  // Handle view tab change - reset to page 1
  const handleViewChange = (view: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (view === 'all') {
      newParams.delete('view');
    } else {
      newParams.set('view', view);
    }
    // Phase 6: Reset to page 1 when view changes
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Handle search change - reset to page 1
  const handleSearchChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    // Phase 6: Reset to page 1 when search changes (combined to avoid race condition)
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Phase 9.13b: Handle sort change (clicking column headers)
  const handleSort = (column: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (sortBy === column) {
      // Toggle order if same column
      newParams.set('order', sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to desc
      newParams.set('sort', column);
      newParams.set('order', 'desc');
    }
    // Reset pagination when sort changes
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Handle clear all filters
  const handleClearAll = () => {
    // Clear all params (search, view, page, etc.)
    setSearchParams(new URLSearchParams());
  };

  // Batch selection helpers
  const allCurrentIds = invoices.map((inv) => inv.id);
  const allSelected = allCurrentIds.length > 0 && allCurrentIds.every((id) => selectedIds.has(id));
  const someSelected = allCurrentIds.some((id) => selectedIds.has(id)) && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      // Deselect all on this page
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allCurrentIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      // Select all on this page
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allCurrentIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirmDateChange = () => {
    const paddedMonth = String(datePickerMonth).padStart(2, '0');
    const invoiceDate = `${datePickerYear}-${paddedMonth}-01`;
    batchUpdateDate.mutate(
      { invoice_ids: Array.from(selectedIds), invoice_date: invoiceDate },
      {
        onSuccess: () => {
          setSelectedIds(new Set());
          setShowDateModal(false);
        },
      }
    );
  };

  // Phase 6: Handle page overflow - redirect to last valid page if current page exceeds total
  useEffect(() => {
    if (!isLoading && data && paginationValues.totalPages > 0) {
      if (page > paginationValues.totalPages) {
        setPage(paginationValues.totalPages);
      }
    }
  }, [page, paginationValues.totalPages, isLoading, data, setPage]);

  // Phase 6: Use delete hook with optimistic updates and total count handling
  const deleteMutation = useDeleteInvoice();

  const handleDeleteClick = (invoice: Invoice, e: React.MouseEvent) => {
    e.stopPropagation();
    setInvoiceToDelete(invoice);
  };

  const confirmDelete = () => {
    if (invoiceToDelete) {
      deleteMutation.mutate(invoiceToDelete.id);
      setInvoiceToDelete(null); // Clear dialog state
    }
  };

  // SPEC-invoice-merge-2026-08-17 R3: open the confirmation for exactly the
  // two selected invoices (AC1). The merge button that dispatches this is
  // only rendered when selectedIds.size === 2 (AC3), so this always has a
  // valid pair.
  const handleOpenMergeDialog = () => {
    const candidates = invoices.filter((inv) => selectedIds.has(inv.id));
    if (candidates.length !== 2) return; // defensive; button is gated on size===2
    if (reviewedMerge) {
      setReviewPair({ a: candidates[0].id, b: candidates[1].id, notice: null });
      return;
    }
    setMergeError(null);
    setMergeCandidates([candidates[0], candidates[1]]);
  };

  // A Review merge opened from one invoice (selected row, or the row action) chooses the other invoice first.
  const openReviewFor = (invoice: Invoice) => setReviewAnchor(invoice);

  // The reviewed flow's wrong-project refusal offers the existing cancel flow; the cancel flow's own
  // server check decides whether that invoice can be cancelled.
  const handleCancelFromReview = (invoiceId: string) => {
    const target = invoices.find((inv) => inv.id === invoiceId);
    setReviewPair(null);
    setReviewAnchor(null);
    if (target) setCancelTarget(target);
    else navigate(`/invoices/${invoiceId}`);
  };

  // Fetch each candidate's line items (not the merge endpoint) so the
  // confirmation can show survivor/retired/line-count/total BEFORE anything
  // happens (AC4) -- there is no separate preview endpoint, and calling the
  // merge endpoint itself would mutate on success. See lib/invoice-merge.ts.
  const [mergeA, mergeB] = mergeCandidates ?? [null, null];
  const mergeItemsA = useInvoiceItems(mergeA?.id);
  const mergeItemsB = useInvoiceItems(mergeB?.id);

  const [mergeSurvivor, mergeAbsorbed] = mergeA && mergeB
    ? determineMergeSurvivor(mergeA, mergeB)
    : [null, null];
  // Refuse visibly BEFORE confirmation when the pair is already known to be
  // ineligible. Mirrors invoice_merge_eligibility's DIFFERENT_PROJECT and
  // DIFFERENT_CLIENT refusals, word for word where it can; the backend stays
  // the authority. Jason's rule: merging is only allowed on invoices against
  // the same project.
  const mergeBlockedReason: string | null = (() => {
    if (!mergeA || !mergeB) return null;
    if (mergeA.project_id !== mergeB.project_id) {
      const a = mergeA.project_name || mergeA.project_id;
      const b = mergeB.project_name || mergeB.project_id;
      return (
        `Invoice ${mergeA.invoice_number} is on project '${a}' and invoice ` +
        `${mergeB.invoice_number} is on project '${b}'; merging would produce one ` +
        `invoice spanning two projects. Keep them as two separate invoices instead. ` +
        `If one of them is on the wrong project, recreate it on the right project, then cancel the wrong one.`
      );
    }
    if ((mergeA.organization_id ?? null) !== (mergeB.organization_id ?? null)) {
      return (
        `Invoice ${mergeA.invoice_number} and invoice ${mergeB.invoice_number} are ` +
        `billed to different clients; only invoices for the same client can be merged.`
      );
    }
    return null;
  })();

  // Cancel-invoice feature. Christa-lens redirect (2026-09-15): offer
  // "Cancel invoice #N" ONLY on the different-project refusal -- its message
  // is the only one that says "...recreate it on the right project, then
  // cancel the wrong one." On the different-client refusal both invoices can
  // be entirely legitimate (billed to different clients is not a mistake on
  // either one) and that message never mentions cancelling, so no offer is
  // made there. Cancellable mirrors the backend's own checks: not already
  // cancelled, no active payment.
  const mergeBlockedIsDifferentProject = !!(
    mergeA && mergeB && mergeA.project_id !== mergeB.project_id
  );
  const mergeBlockedInvoices = canDeleteInvoices && mergeBlockedIsDifferentProject && mergeA && mergeB
    ? [mergeA, mergeB].map((inv) => ({
        id: inv.id,
        invoiceNumber: inv.invoice_number,
        // SPEC-invoice-merge-history R4 AC7: no lifecycle/financial eligibility decision here any more;
        // the cancel flow (CancelInvoiceDialog -> server) refuses a cancel it cannot do, with its reason.
        cancellable: true,
      }))
    : [];

  const handleCancelFromBlockedMerge = (invoiceId: string) => {
    const target = [mergeA, mergeB].find((inv) => inv?.id === invoiceId);
    if (!target) return;
    setMergeCandidates(null);
    setMergeError(null);
    setCancelTarget(target);
  };
  const mergePreviewLoading = mergeItemsA.isLoading || mergeItemsB.isLoading;
  const mergeMovedLineItemCount = mergeAbsorbed
    ? (mergeAbsorbed.id === mergeA?.id ? mergeItemsA.data?.invoice_items?.length : mergeItemsB.data?.invoice_items?.length)
      ?? null
    : null;
  const mergeResultingTotal = !mergePreviewLoading && mergeItemsA.data && mergeItemsB.data
    ? computeMergeResultingTotal([
        ...(mergeItemsA.data.invoice_items ?? []),
        ...(mergeItemsB.data.invoice_items ?? []),
      ])
    : null;

  // What the CLIENT will read on the combined invoice. Mirrors the backend's
  // _carry_po_number and _combine_external_notes so the confirmation shows the
  // same result the transaction will produce.
  //
  // The PO matters most: a PO can live in the client-facing notes as well as in
  // the po_number column -- that is where they lived before the column existed
  // and where she was told to put them. When one invoice carries a real
  // po_number and the other carries a PO in its notes, the backend's conflict
  // refusal cannot see it and BOTH reach the combined bill. A merge does not
  // pass through the PDF preview she performs on every invoice, so this dialog
  // is the only place she can catch it.
  const mergeResultingPoNumber = mergeSurvivor && mergeAbsorbed
    ? (mergeSurvivor.po_number?.trim() || mergeAbsorbed.po_number?.trim() || null)
    : null;
  const mergeResultingExternalNotes = mergeSurvivor && mergeAbsorbed
    ? (() => {
        const survivorNotes = (mergeSurvivor.external_notes ?? '').trim();
        const absorbedNotes = (mergeAbsorbed.external_notes ?? '').trim();
        if (!absorbedNotes) return survivorNotes || null;
        if (!survivorNotes) return absorbedNotes;
        if (survivorNotes.includes(absorbedNotes)) return survivorNotes;
        return `${survivorNotes}
${absorbedNotes}`;
      })()
    : null;

  const handleConfirmMerge = () => {
    if (!mergeA || !mergeB) return;
    mergeMutation.mutate(
      { invoice_id_a: mergeA.id, invoice_id_b: mergeB.id },
      {
        onSuccess: () => {
          setMergeCandidates(null);
          setMergeError(null);
          setSelectedIds(new Set());
          toast.success('Invoices merged successfully');
        },
        onError: (error: any) => {
          // SPEC-invoice-merge-history R4 AC7: an OLD client (a page loaded before the tenant flag was
          // turned on) calling the legacy merge is told to review, and is taken to the review for this
          // very pair -- not shown a bare refusal. Nothing was merged.
          if (isMergeReviewRequired(error)) {
            setMergeCandidates(null);
            setMergeError(null);
            setReviewPair({
              a: mergeA.id,
              b: mergeB.id,
              notice:
                'Merging now needs a review so you can see what changes and what happens to payments. Nothing was merged. Review it here, then combine.',
            });
            return;
          }
          // Dialog stays open (AC7, AC9): both invoices are unchanged and
          // the user sees R1's specific reason rather than a silent no-op
          // or a lost selection.
          setMergeError(extractMergeRefusal(error));
        },
      }
    );
  };

  const handleCancelMergeDialog = (open: boolean) => {
    if (!open) {
      setMergeCandidates(null);
      setMergeError(null);
    }
  };

  // Shared DataTable column config (Phase 9.13b sort + 10a.8 responsive + R7).
  // Responsive priorities preserved from the inline 10a.8 treatment:
  // md: Status · lg: Project + Paid · xl: Sent + Due Date.
  // Checkbox + Invoice # freeze at lg+ during horizontal scroll.
  const columns: DataTableColumn<Invoice>[] = [
    // Batch select checkbox
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          aria-label="Select all invoices on this page"
          checked={allSelected}
          ref={(el) => { if (el) el.indeterminate = someSelected; }}
          onChange={toggleSelectAll}
          className="h-4 w-4 cursor-pointer rounded border-input accent-primary"
        />
      ),
      width: 'w-10',
      sticky: true,
      stickyLeft: 'lg:left-0',
      onCellClick: (invoice, e) => {
        e.stopPropagation();
        // Only toggle on cell-padding clicks. A click on the input itself
        // fires its own onChange — toggling here too double-toggles back to
        // unselected (pre-existing bug surfaced by 2026-06-03 staging QA).
        if ((e.target as HTMLElement).tagName !== 'INPUT') {
          toggleSelectOne(invoice.id);
        }
      },
      render: (invoice) => (
        <input
          type="checkbox"
          aria-label={`Select invoice ${invoice.invoice_number}`}
          checked={selectedIds.has(invoice.id)}
          onChange={() => toggleSelectOne(invoice.id)}
          className="h-4 w-4 cursor-pointer rounded border-input accent-primary"
        />
      ),
    },
    // QW-2: Reordered columns - Client before Project (Christa feedback 2024-12-22)
    {
      key: 'invoice_number',
      label: 'Invoice #',
      sortable: true,
      sortKey: 'invoice_number',
      sticky: true,
      stickyLeft: 'lg:left-8',
      cellClassName: 'font-medium',
      render: (invoice) => (
        <>
          {invoice.invoice_number}
          {/* SPEC-invoice-merge-2026-08-17 R3 AC8: a merged invoice must not
              silently vanish from view -- it stays in the list with a way to
              trace where it went, per R1's retirement design (status=cancelled
              + merged_into_invoice_id + a human-readable internal_notes line).
              Re-homed 2026-09-04 from the old inline table into main's shared
              DataTable columns config (R7). */}
          {invoice.merged_into_invoice_id && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    data-testid={`merged-indicator-${invoice.invoice_number}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/invoices/${invoice.merged_into_invoice_id}`);
                    }}
                    className="block text-xs text-blue-600 hover:underline"
                  >
                    {invoice.merged_into_invoice_number
                      ? `Merged into ${invoice.merged_into_invoice_number}`
                      : 'Merged'}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {invoice.merged_into_invoice_number
                    ? `This invoice was merged into invoice ${invoice.merged_into_invoice_number}. Open it to see the current invoice; this invoice’s original history stays available.`
                    : invoice.internal_notes || 'This invoice was merged into another invoice.'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </>
      ),
    },
    // 10a.8: Merged Client + DBA column with truncation + tooltip
    {
      key: 'client',
      label: 'Client',
      sortable: true,
      sortKey: 'client_name',
      maxWidth: 'max-w-[140px] lg:max-w-[200px]',
      render: (invoice) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="min-w-0">
                <span className="block truncate">{invoice.client_legal_name || invoice.client_name || '-'}</span>
                {invoice.establishment_name && (
                  <span className="block truncate text-sm text-blue-600" data-testid="establishment-name-label">{invoice.establishment_name}</span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{invoice.client_legal_name || invoice.client_name || '-'}</p>
              {invoice.establishment_name && (
                <p className="text-muted-foreground">{invoice.establishment_name}</p>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    // R7: Recipient column — TO/CC contacts from invoice_contacts snapshot
    {
      key: 'recipient',
      label: 'Recipient',
      render: (invoice) => (
        <div data-testid={`recipient-cell-${invoice.invoice_number}`}>
          <RecipientCell contacts={invoice.invoice_contacts ?? []} />
        </div>
      ),
    },
    // 10a.8: Project column with truncation + tooltip + responsive hiding
    {
      key: 'project',
      label: 'Project',
      sortable: true,
      sortKey: 'project_name',
      responsive: 'lg',
      maxWidth: 'max-w-[180px]',
      cellClassName: 'text-muted-foreground',
      render: (invoice) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="block truncate">
                {invoice.project_name}
                {invoice.project_is_archived && (
                  <span className="ml-1 inline-flex items-center rounded-sm bg-muted px-1 py-0.5 text-[10px] font-medium text-muted-foreground">Archived</span>
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{invoice.project_name}{invoice.project_is_archived ? ' (Archived)' : ''}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      sortKey: 'total_amount',
      align: 'right',
      cellClassName: 'font-mono',
      render: (invoice) => (
        <>
          ${invoice.total_amount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </>
      ),
    },
    // Phase 9.13c: Paid column showing amount_paid | 10a.8: hidden below lg
    {
      key: 'paid',
      label: 'Paid',
      align: 'right',
      responsive: 'lg',
      cellClassName: 'font-mono text-muted-foreground',
      render: (invoice) =>
        (invoice.amount_paid ?? 0) > 0 ? (
          <>
            ${(invoice.amount_paid ?? 0).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            {(invoice.amount_paid ?? 0) < invoice.total_amount && (
              <span className="text-xs ml-1">
                ({Math.round(((invoice.amount_paid ?? 0) / invoice.total_amount) * 100)}%)
              </span>
            )}
            {/* R4 AC5: money received above the total is shown as excess held, never as a percentage over 100. */}
            {(invoice.amount_paid ?? 0) > invoice.total_amount && (
              <span className="text-xs ml-1" data-testid={`excess-paid-${invoice.invoice_number}`}>
                (excess ${((invoice.amount_paid ?? 0) - invoice.total_amount).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })})
              </span>
            )}
          </>
        ) : (
          '-'
        ),
    },
    // Phase 6 Batch 5.3: Inline invoice status editing
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      sortKey: 'status',
      responsive: 'md',
      onCellClick: (_invoice, e) => e.stopPropagation(),
      render: (invoice) =>
        editingInvoiceId === invoice.id ? (
          <InvoiceStatusSelect
            value={invoice.status as InvoiceStatus}
            onChange={(newStatus) => {
              updateInvoiceInline.mutate({
                id: invoice.id,
                data: { status: newStatus }
              });
              setEditingInvoiceId(null);
            }}
            onCancel={() => setEditingInvoiceId(null)}
            disabled={updateInvoiceInline.isPending}
            className="w-[150px]"
            amountPaid={invoice.amount_paid || 0}
            // Theme 5b: the list row only knows the total (no per-item data), so
            // we can still gate the common $0 doomed case. The sum-mismatch case
            // is only enforceable on the detail page where items are loaded.
            invoiceTotal={Number(invoice.total_amount) || 0}
            // Open immediately so closing without a pick cancels back to the
            // badge — otherwise clicking away strands the row in edit mode.
            defaultOpen
          />
        ) : (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setEditingInvoiceId(invoice.id);
            }}
            title="Click to change status"
            data-testid={`invoice-status-badge-${invoice.invoice_number}`}
          >
            <InvoiceStatusBadge
              status={invoice.status as InvoiceStatus}
              editable={true}
              amountPaid={invoice.amount_paid || 0}
              totalAmount={invoice.total_amount}
              // v0.14.4 R5: the select closes immediately on change, so the
              // in-flight PUT would otherwise show the optimistic value with no
              // cue. Drive the badge spinner from the mutation's own variables so
              // the row in flight (and only that row) shows "saving…" until the
              // PUT settles or rolls back.
              pending={
                updateInvoiceInline.isPending &&
                updateInvoiceInline.variables?.id === invoice.id
              }
            />
          </div>
        ),
    },
    // Phase 9.26: Sent date column | 10a.8: xl only
    {
      key: 'sent',
      label: 'Sent',
      responsive: 'xl',
      cellClassName: 'text-muted-foreground',
      // R3 AC8 (production finding l, invoice 0757): a date only when a send is RECORDED. The column used to fall
      // back to status_changed_at for any non-draft invoice, which for a Ready invoice that was never sent is the Ready
      // date. It reads the one send rule (send_evidence); a payment or a status change never makes a send date. An
      // older server without send_evidence still reads email_sent_at only.
      render: (invoice) => {
        const sentAt = recordedSentAt(invoice);
        return sentAt ? new Date(sentAt).toLocaleDateString() : '-';
      },
    },
    {
      key: 'due_date',
      label: 'Due Date',
      sortable: true,
      sortKey: 'due_date',
      responsive: 'xl',
      cellClassName: 'text-muted-foreground',
      render: (invoice) =>
        invoice.due_date ? new Date(invoice.due_date + 'T00:00:00').toLocaleDateString() : '-',
    },
    // Actions
    {
      key: 'actions',
      label: '',
      width: 'w-24',
      render: (invoice) => (
        <div className="flex items-center gap-1">
          {/* Phase 9.13c: Record Payment button for sent/overdue/partially_paid invoices */}
          {(invoice.status === 'sent' || invoice.status === 'overdue' || invoice.status === 'partially_paid') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setPaymentInvoice(invoice);
              }}
              className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700"
              title="Record Payment"
            >
              <DollarSign className="h-4 w-4" />
            </Button>
          )}
          {/* R4 AC1: a discoverable Review merge entry on every row, in all six states. A merged-away
              invoice shows the "Merged into" link instead (its current invoice is where review starts). */}
          {reviewedMerge && canDeleteInvoices && !invoice.merged_into_invoice_id && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                openReviewFor(invoice);
              }}
              className="h-8 w-8 p-0"
              title="Review merge"
              aria-label={`Review merge for invoice ${invoice.invoice_number}`}
              data-testid={`review-merge-row-${invoice.invoice_number}`}
            >
              <Combine className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/invoices/${invoice.id}`);
            }}
            className="h-8 w-8 p-0 hidden lg:inline-flex"
            title="View Invoice"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {canDeleteInvoices && (() => {
            const rowDeleteBlockedReason = getDeleteBlockedReason(invoice.status);
            return (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => handleDeleteClick(invoice, e)}
                disabled={deleteMutation.isPending || !!rowDeleteBlockedReason}
                title={rowDeleteBlockedReason || 'Delete'}
                data-testid={`delete-invoice-row-${invoice.invoice_number}`}
                className="text-destructive hover:text-destructive h-8 w-8 p-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            );
          })()}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <DashboardLayout title="Invoices">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
    <DashboardLayout
      title="Invoices"
      actions={
        <div className="flex gap-2">
          <QboCsvExportDialog />
          <Button variant="outline" onClick={() => navigate('/invoices/billing-session')}>
            <Zap className="mr-2 h-4 w-4" />
            Billing Session
          </Button>
          <Button onClick={() => navigate('/invoices/new')}>
            <Plus className="mr-2 h-4 w-4" />
            New Invoice
          </Button>
        </div>
      }
    >
      <div className="mb-4">
        <p className="text-muted-foreground">
          Track and manage project invoices
        </p>
      </div>

      {/* View Tabs */}
      <ViewTabs
        views={invoiceViews}
        currentView={currentView}
        onChange={handleViewChange}
      />

      {/* Filter Bar */}
      <FilterBar
        search={search}
        onSearchChange={handleSearchChange}
        // "Search invoices..." named none of the nine things it searches, so
        // there was no way to know an address would work without guessing --
        // Christa tried one and concluded it "wouldn't let me" (2026-08-04).
        // Naming address and project number is the whole point of this label.
        placeholder="Search by invoice #, project, address, or client..."
        onClearAll={handleClearAll}
        activeFilterCount={search ? 1 : 0}
      />

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Failed to load invoices: {(error as Error).message}
          </p>
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 rounded-md border bg-muted/50 px-4 py-2 text-sm">
          <span className="font-medium">{selectedIds.size} invoice{selectedIds.size !== 1 ? 's' : ''} selected</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDateModal(true)}
            className="flex items-center gap-1.5"
          >
            <CalendarDays className="h-4 w-4" />
            Change Date
          </Button>
          {/* SPEC-invoice-merge-2026-08-17 R3 AC1a/AC3: only offered for
              EXACTLY two selected invoices. Named for what it does -- this
              combines two invoices into one, not "link" (Christa's guess at
              the button's name; see R3-merge-ui.md). */}
          {selectedIds.size === 2 && (!reviewedMerge || canDeleteInvoices) && (
            <Button
              variant="outline"
              size="sm"
              data-testid="merge-invoices-button"
              onClick={handleOpenMergeDialog}
              className="flex items-center gap-1.5"
            >
              <Combine className="h-4 w-4" />
              {reviewedMerge ? 'Review merge' : 'Merge Invoices'}
            </Button>
          )}
          {/* R4 AC1: one selected invoice is enough to start -- the other is chosen from the same project. */}
          {reviewedMerge && canDeleteInvoices && selectedIds.size === 1 && (
            <Button
              variant="outline"
              size="sm"
              data-testid="review-merge-with-button"
              onClick={() => {
                const only = invoices.find((inv) => selectedIds.has(inv.id));
                if (only) openReviewFor(only);
              }}
              className="flex items-center gap-1.5"
            >
              <Combine className="h-4 w-4" />
              Review merge…
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto flex items-center gap-1 text-muted-foreground"
          >
            <X className="h-4 w-4" />
            Clear selection
          </Button>
        </div>
      )}

      {/* Table — shared DataTable: same 10a.8 responsive treatment + sticky
          mirror scrollbar, now driven by the columns config above */}
      {!error && (
        <DataTable<Invoice>
          columns={columns}
          rows={invoices}
          rowKey={(inv) => inv.id}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onRowClick={(invoice) => {
            storeCurrentPage(page); // 9.15c: Store page before navigation
            navigate(`/invoices/${invoice.id}`);
          }}
          rowClassName={(invoice) => (selectedIds.has(invoice.id) ? 'bg-muted/30' : undefined)}
          emptyState={
            search ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <p>No invoices found</p>
                <p className="text-sm mt-1">Try adjusting your search or filters</p>
              </div>
            ) : (
              <EmptyState
                icon={Receipt}
                title="No invoices yet"
                description="Create an invoice from your confirmed billable items"
                actionLabel="Create Invoice"
                actionUrl="/invoices/new"
              />
            )
          }
          scrollContainerRef={tableContainerRef}
          containerTestId="invoices-table"
        />
      )}

      {/* Phase 6: Pagination Footer */}
      {!error && total > 0 && (
        <div className="flex items-center justify-between">
          <PaginationInfo
            total={total}
            start={paginationValues.start}
            end={paginationValues.end}
            itemName="invoices"
          />

          {paginationValues.totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => handlePageChange(page - 1)}
                    className={!paginationValues.hasPreviousPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>

                {/* First page */}
                {paginationValues.currentPage > 2 && (
                  <PaginationItem>
                    <PaginationLink onClick={() => handlePageChange(1)} className="cursor-pointer">
                      1
                    </PaginationLink>
                  </PaginationItem>
                )}

                {/* Left ellipsis */}
                {paginationValues.currentPage > 3 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                {/* Previous page */}
                {paginationValues.hasPreviousPage && (
                  <PaginationItem>
                    <PaginationLink onClick={() => handlePageChange(page - 1)} className="cursor-pointer">
                      {page - 1}
                    </PaginationLink>
                  </PaginationItem>
                )}

                {/* Current page */}
                <PaginationItem>
                  <PaginationLink isActive className="cursor-default">
                    {page}
                  </PaginationLink>
                </PaginationItem>

                {/* Next page */}
                {paginationValues.hasNextPage && (
                  <PaginationItem>
                    <PaginationLink onClick={() => handlePageChange(page + 1)} className="cursor-pointer">
                      {page + 1}
                    </PaginationLink>
                  </PaginationItem>
                )}

                {/* Right ellipsis */}
                {paginationValues.currentPage < paginationValues.totalPages - 2 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                {/* Last page */}
                {paginationValues.currentPage < paginationValues.totalPages - 1 && (
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => handlePageChange(paginationValues.totalPages)}
                      className="cursor-pointer"
                    >
                      {paginationValues.totalPages}
                    </PaginationLink>
                  </PaginationItem>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => handlePageChange(page + 1)}
                    className={!paginationValues.hasNextPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      )}
    </DashboardLayout>

    {/* SPEC-invoice-merge-2026-08-17 R3: Merge Confirmation Dialog */}
    {mergeSurvivor && mergeAbsorbed && (
      <MergeConfirmationDialog
        open={!!mergeCandidates}
        onOpenChange={handleCancelMergeDialog}
        clientName={mergeSurvivor.client_name}
        projectName={mergeSurvivor.project_name}
        blockedReason={mergeBlockedReason}
        blockedInvoices={mergeBlockedInvoices}
        onCancelInvoice={handleCancelFromBlockedMerge}
        resultingPoNumber={mergeResultingPoNumber}
        resultingExternalNotes={mergeResultingExternalNotes}
        survivorInvoiceNumber={mergeSurvivor.invoice_number}
        absorbedInvoiceNumber={mergeAbsorbed.invoice_number}
        movedLineItemCount={mergeMovedLineItemCount}
        resultingTotal={mergeResultingTotal}
        pending={mergeMutation.isPending}
        error={mergeError}
        onConfirm={handleConfirmMerge}
      />
    )}

    {/* SPEC-invoice-merge-history R4: the server-plan review (flag on) for two selected invoices, or the
        candidate picker then review when started from one invoice. */}
    {reviewPair && (
      <InvoiceMergeReview
        invoiceIdA={reviewPair.a}
        invoiceIdB={reviewPair.b}
        notice={reviewPair.notice}
        onClose={() => setReviewPair(null)}
        onMerged={() => setSelectedIds(new Set())}
        onCancelInvoice={handleCancelFromReview}
      />
    )}
    {reviewAnchor && (
      <InvoiceMergeFlow
        anchor={{
          id: reviewAnchor.id,
          invoice_number: reviewAnchor.invoice_number,
          project_id: reviewAnchor.project_id,
          project_name: reviewAnchor.project_name,
        }}
        onClose={() => setReviewAnchor(null)}
        onMerged={() => setSelectedIds(new Set())}
        onCancelInvoice={handleCancelFromReview}
      />
    )}

    {/* Cancel-invoice feature: reached from a blocked merge's "Cancel invoice
        #N" buttons above. */}
    {cancelTarget && (
      <CancelInvoiceDialog
        open={!!cancelTarget}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
          else setCancelError(null);
        }}
        invoiceNumber={cancelTarget.invoice_number}
        projectName={cancelTarget.project_name}
        clientName={cancelTarget.client_name}
        totalAmount={cancelTarget.total_amount}
        qboExportedAt={cancelTarget.qbo_csv_exported_at ?? null}
        pending={cancelMutation.isPending}
        error={cancelError}
        onConfirm={(reason) => {
          setCancelError(null);
          if (isSuperAdmin && isGuardedStatusDowngrade(cancelTarget.status, 'cancelled')) {
            setPendingCancelSuperAdmin({ invoice: cancelTarget, reason });
            setCancelTarget(null);
            return;
          }
          cancelMutation.mutate(
            { id: cancelTarget.id, data: { reason } },
            {
              onSuccess: () => setCancelTarget(null),
              onError: (err: any) => setCancelError({ message: err?.message || 'Failed to cancel invoice' }),
            },
          );
        }}
      />
    )}
    <SuperAdminDestructiveConfirmDialog
      open={pendingCancelSuperAdmin !== null}
      onOpenChange={(open) => {
        if (!open) setPendingCancelSuperAdmin(null);
      }}
      opDescription={pendingCancelSuperAdmin ? `cancel ${statusLabel(pendingCancelSuperAdmin.invoice.status)} invoice` : ''}
      entityLabel={pendingCancelSuperAdmin ? `invoice ${pendingCancelSuperAdmin.invoice.invoice_number}` : ''}
      confirmLabel="Confirm cancel"
      isPending={cancelMutation.isPending}
      onConfirm={() => {
        if (!pendingCancelSuperAdmin) return;
        cancelMutation.mutate(
          {
            id: pendingCancelSuperAdmin.invoice.id,
            data: { reason: pendingCancelSuperAdmin.reason },
            confirmDestructiveEntityId: pendingCancelSuperAdmin.invoice.id,
          },
          { onSettled: () => setPendingCancelSuperAdmin(null) },
        );
      }}
    />

    {/* Delete Confirmation Dialog */}
    <AlertDialog open={!!invoiceToDelete} onOpenChange={() => setInvoiceToDelete(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete invoice "{invoiceToDelete?.invoice_number}"? This action cannot be undone.
            {invoiceToDelete?.status === 'paid' && (
              <span className="block mt-2 text-orange-600">
                Note: Paid invoices cannot be deleted.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirmDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Phase 9.13c: Inline Payment Modal */}
    {paymentInvoice && (
      <PaymentModal
        open={!!paymentInvoice}
        invoiceId={paymentInvoice.id}
        totalAmount={paymentInvoice.total_amount}
        amountPaid={paymentInvoice.amount_paid ?? 0}
        // 2026-08-03 reconciliation fix: the $ action opens this modal straight
        // from a row, so the modal has to say WHICH invoice it is paying. Same
        // name resolution as the Client column above.
        invoiceNumber={paymentInvoice.invoice_number}
        clientName={paymentInvoice.client_legal_name || paymentInvoice.client_name}
        onOpenChange={(open) => {
          if (!open) setPaymentInvoice(null);
        }}
        onSuccess={() => {
          setPaymentInvoice(null);
          // Data refreshes automatically via React Query cache invalidation
        }}
      />
    )}

    {/* Batch Date Change Modal */}
    <Dialog open={showDateModal} onOpenChange={setShowDateModal}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Change Invoice Date</DialogTitle>
          <DialogDescription>
            Set the invoice date for {selectedIds.size} selected invoice{selectedIds.size !== 1 ? 's' : ''}.
            The date will be set to the 1st of the selected month.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 py-4">
          <div className="flex-1">
            <label className="mb-1.5 block text-sm font-medium">Month</label>
            <select
              value={datePickerMonth}
              onChange={(e) => setDatePickerMonth(Number(e.target.value))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {[
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December',
              ].map((name, idx) => (
                <option key={idx + 1} value={idx + 1}>{name}</option>
              ))}
            </select>
          </div>
          <div className="w-28">
            <label className="mb-1.5 block text-sm font-medium">Year</label>
            <select
              value={datePickerYear}
              onChange={(e) => setDatePickerYear(Number(e.target.value))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((yr) => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowDateModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDateChange}
            disabled={batchUpdateDate.isPending}
          >
            {batchUpdateDate.isPending ? 'Updating...' : 'Update Dates'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
